<template>
  <div class="retinoscopy-receipt w-full" :class="{ compact, 'rr-tall': !compact, 'rr-a5': a5 }" dir="ltr">
      <div
          v-for="section in visibleRetinoscopySections"
          :key="section.key"
          class="rr-block"
          :class="{ 'rr-empty': !section.filled }"
      >
        <div class="rr-title rr-title-row" :style="titleStyle">
          <div>{{ section.title }}</div>
          <div v-if="section.workingDistances.length === 1" class="rr-wd">
            WD {{ workingDistanceLabel(section.workingDistances[0]) }}
          </div>
        </div>
        <div class="rr-cross-row">
          <div
              v-for="(eye, eyeIndex) in eyes"
              :key="eye"
              class="rr-cross-wrapper"
              :class="{ 'rr-cross-divider': eyeIndex > 0 }"
          >
            <div class="rr-eye-label">
              <span class="font-bold">{{ eye.toUpperCase() }}</span>
              <span class="rr-eye-name">{{ eye === 'od' ? 'Right eye' : 'Left eye' }}</span>
            </div>
            <div class="rr-cross">
              <div class="rr-axis-v"></div>
              <div class="rr-axis-h"></div>
              <span class="rr-label-90">{{ retinoscopyAxes(data[section.key][eye])[0] }}°</span>
              <span class="rr-label-180">{{ retinoscopyAxes(data[section.key][eye])[1] }}°</span>
              <template v-for="meridian in meridians" :key="meridian">
                <div
                    v-if="data[section.key][eye][meridian].gross != null"
                    class="rr-meridian"
                    :class="`rr-${meridian}`"
                >
                  <span class="rr-caption">Gross</span>
                  <span class="rr-value">{{ formatDiopter(data[section.key][eye][meridian].gross) }}</span>
                  <template v-if="section.workingDistances.length > 1">
                    <span class="rr-caption">WD</span>
                    <span class="rr-value">{{ formatDiopter(data[section.key][eye][meridian].workingDistance) }}</span>
                  </template>
                  <div class="rr-line"></div>
                  <span class="rr-caption">Net</span>
                  <span class="rr-value rr-net">{{ formatDiopter(data[section.key][eye][meridian].net) }}</span>
                </div>
              </template>
            </div>
            <div class="rr-rx">
              <span class="rr-caption">Rx</span>
              <span v-if="rxText(section.key, eye)" class="rr-value rr-rx-box">{{ rxText(section.key, eye) }}</span>
            </div>
          </div>
        </div>
      </div>

    <div v-if="notes && !compact" class="rr-block">
      <div class="rr-title rr-notes-title" :style="titleStyle">
        <span>Notes</span>
        <span v-if="arabic" class="rr-title-ar">الملاحظات</span>
      </div>
      <div class="rr-notes" dir="auto">{{ notes }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, type PropType} from 'vue'
import {useSettingsStore} from '@/stores/settingsStore'
import {
  CYL_NOTATION_SETTING_KEY,
  cylNotationFromSetting,
  sectionCylNotation,
  formatDiopter,
  formatRx,
  normalizeRetinoscopy,
  retinoscopyHasValues,
  retinoscopyAxes,
  retinoscopyRx,
  retinoscopyWorkingDistances,
  workingDistanceLabel,
  type Retinoscopy
} from '@/models/customer/Examination'

const props = defineProps({
  retinoscopy: {
    type: Object as PropType<Retinoscopy | null>,
    default: null
  },
  notes: {
    type: String,
    default: ''
  },
  brandingColor: {
    type: String,
    default: '#14919B'
  },
  compact: {
    type: Boolean,
    default: false
  },
  // A5 reuses the A4 page zoomed down, so the crosses shrink to leave room for the notes
  a5: {
    type: Boolean,
    default: false
  },
  hideEmptySections: {
    type: Boolean,
    default: true
  },
  arabic: {
    type: Boolean,
    default: false
  }
})

const eyes = ['od', 'os'] as const
const meridians = ['meridian90', 'meridian180'] as const

const retinoscopySections = [
  {key: 'dryRetinoscopy', title: 'Dry Retinoscopy'},
  {key: 'cycloplegicRetinoscopy', title: 'Cycloplegic Retinoscopy'}
] as const

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = computed(() => normalizeRetinoscopy(props.retinoscopy) as any)
const settingsStore = useSettingsStore()
const cylNotation = computed(() => cylNotationFromSetting(settingsStore.getValue(CYL_NOTATION_SETTING_KEY)))

const titleStyle = computed(() => ({borderColor: props.brandingColor}))

function rxText(key: typeof retinoscopySections[number]['key'], eye: typeof eyes[number]): string {
  return formatRx(retinoscopyRx(data.value[key][eye], sectionCylNotation(data.value[key], cylNotation.value)))
}

const visibleRetinoscopySections = computed(() => retinoscopySections
    .filter(section => !props.hideEmptySections || retinoscopyHasValues(data.value[section.key]))
    .map(section => ({
      ...section,
      filled: retinoscopyHasValues(data.value[section.key]),
      workingDistances: retinoscopyWorkingDistances(data.value[section.key])
    })))

</script>

<style scoped>
.retinoscopy-receipt {
  @apply px-8 mt-2 flex flex-col gap-2;
  font-size: calc(var(--rr-body-scale, 1) * 1em);
}

.rr-title {
  @apply font-bold text-[#555] mb-1 ps-2;
  border-left: 3px solid;
}

.rr-cross-row {
  font-size: 0.9em;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.rr-cross-wrapper {
  @apply flex flex-col px-4;
  min-width: 0;
}

.rr-cross-wrapper:first-child {
  padding-left: 0;
}

.rr-cross-divider {
  border-left: 1px solid #9b9b9b;
}

.rr-eye-label {
  line-height: 1.2;
}

.rr-eye-name {
  @apply text-[#686868] mx-1;
  font-size: 0.7em;
}

.rr-cross {
  position: relative;
  width: 100%;
  height: 11em;
}

.rr-axis-v {
  position: absolute;
  left: 2.6em;
  top: 0;
  bottom: 0;
  border-left: 2px solid #000;
}

.rr-axis-h {
  position: absolute;
  left: 1em;
  right: 0;
  bottom: 1.6em;
  border-top: 2px solid #000;
}

.rr-label-90 {
  position: absolute;
  left: 0;
  top: 0;
  font-weight: 700;
  font-size: 0.85em;
}

.rr-label-180 {
  position: absolute;
  right: 0;
  bottom: 0.1em;
  font-weight: 700;
  font-size: 0.85em;
}

.rr-meridian {
  position: absolute;
  width: 8.5em;
  display: grid;
  grid-template-columns: 3em 1fr;
  column-gap: 0.4em;
  align-items: center;
  line-height: 1.25;
}

.rr-meridian90 {
  left: 3.1em;
  top: 0.6em;
}

.rr-meridian180 {
  right: 0;
  bottom: 1.8em;
}

.rr-caption {
  @apply text-[#555];
  font-size: 0.65em;
  font-weight: 600;
  text-align: end;
}

.rr-value {
  font-weight: 700;
  text-align: center;
}

.rr-line {
  grid-column: span 2;
  border-top: 1px solid #9b9b9b;
  margin: 1px 0;
}

.rr-net {
  background: #dbecfc;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.rr-title-row {
  display: flex;
  align-items: baseline;
  gap: 1.5em;
}

.rr-title-row > div:first-child {
  font-family: inherit;
  font-weight: inherit;
}

.rr-wd {
  @apply text-[#686868] font-normal;
  font-size: 0.85em;
}

.rr-rx {
  display: flex;
  align-items: baseline;
  gap: 0.5em;
  margin-top: 0.25em;
  color: #1e4f7b;
}

.rr-rx {
  min-height: 1.9em;
  align-items: center;
}

/* The Rx is the result of the cross, so it is boxed like the Net values */
.rr-rx-box {
  background: #dbecfc;
  border: 1px solid #b6d4f2;
  border-radius: 6px;
  padding: 0.1em 0.6em;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Same look as the Eye Exam notes */
.rr-notes-title {
  @apply flex items-baseline justify-between text-[#444];
  border-left-width: 4px;
  padding-left: 0.5rem;
  letter-spacing: 0.02em;
}

.rr-title-ar {
  @apply font-bold text-[#777];
  letter-spacing: 0;
  font-size: 0.85em;
  direction: rtl;
}

.rr-notes {
  @apply rounded-md p-3 whitespace-pre-wrap font-bold;
  background: #f4f8fc;
  border: 1px solid #dbe6f1;
  unicode-bidi: plaintext;
  text-align: start;
  /* Bold like the customer name in the header, slightly smaller */
  font-size: calc(var(--rr-notes-scale, 1) * 1.19em);
  line-height: 1.3;
  color: #111;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.rr-tall .rr-cross-row {
  font-size: 1em;
}

.rr-tall .rr-cross {
  height: 15.5em;
}

/* Blank crosses (print-all mode) are drawn shorter so the page still fits */
.rr-empty .rr-cross {
  height: 7em;
}

.rr-tall .rr-empty .rr-cross {
  height: 9em;
}

.rr-tall .rr-meridian90 {
  top: 0.8em;
}

/* Match the Eye Exam notes title on A5 */
.rr-a5 .rr-notes-title {
  font-size: 0.95em;
}

.compact {
  @apply px-0 mt-1 gap-1;
  font-size: calc(var(--rr-body-scale, 1) * 1.1em);
}

/* A6: the crosses stretch to fill the page down to the footer */
.compact {
  flex: 1 1 auto;
  min-height: 0;
}

.compact .rr-block {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.compact .rr-block.rr-empty {
  flex: 0 0 auto;
}

.compact .rr-cross-row {
  flex: 1 1 auto;
}

.compact .rr-cross {
  flex: 1 1 auto;
  height: auto;
  min-height: 9em;
}

.compact .rr-empty .rr-cross {
  flex: none;
  height: 7em;
  min-height: 0;
}

.compact .rr-title-row {
  display: flex;
  align-items: baseline;
  gap: 1.5em;
}

.rr-wd {
  @apply text-[#686868] font-normal;
  font-size: 0.85em;
}

.rr-rx {
  display: flex;
  align-items: baseline;
  gap: 0.5em;
  margin-top: 0.25em;
  color: #1e4f7b;
}

.rr-notes {
  @apply p-1;
}
</style>
